import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useT } from '../i18n/useT';
import StorageRoundedIcon from '@mui/icons-material/StorageRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';

const BULLET_ICONS = [
  <HubRoundedIcon sx={{ fontSize: '1.45rem', color: '#0F172A' }} />,
  <StorageRoundedIcon sx={{ fontSize: '1.45rem', color: '#0F172A' }} />,
  <ShieldRoundedIcon sx={{ fontSize: '1.45rem', color: '#0F172A' }} />,
];

const ConnectData: React.FC = () => {
  const { t, r } = useT();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const bullets = r.connect.bullets;

  return (
    <Box component="section" id="connect" sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#F8FAFC' }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 4, md: 8 },
            alignItems: 'center',
          }}
        >
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              component="span"
              sx={{
                color: '#64748B',
                fontWeight: 700,
                fontSize: '0.8rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                mb: 2,
                display: 'block',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              }}
            >
              {t('connect.eyebrow')}
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.8rem', md: '2.4rem' },
                mb: 2,
                color: '#0F172A',
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
              }}
            >
              {t('connect.title')}
            </Typography>
            <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.7, fontSize: '1.05rem', mb: 4 }}>
              {t('connect.subtitle')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 4 }}>
              {bullets.map((bullet, idx) => (
                <Box key={bullet.title} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      mt: 0.3,
                      flexShrink: 0,
                      width: 38,
                      height: 38,
                      borderRadius: 1.5,
                      backgroundColor: 'white',
                      border: '1px solid #E2E8F0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {BULLET_ICONS[idx]}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: '#1E293B', fontSize: '1.05rem', mb: 0.5 }}>
                      {bullet.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B', lineHeight: 1.6 }}>
                      {bullet.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                p: 2.5,
                borderRadius: 2,
                backgroundColor: 'white',
                border: '1px solid #E2E8F0',
                borderLeft: '3px solid #0F172A',
              }}
            >
              <Typography
                sx={{
                  color: '#0F172A',
                  fontWeight: 600,
                  fontSize: { xs: '0.95rem', md: '1rem' },
                  lineHeight: 1.5,
                }}
              >
                {t('connect.tagline')}
              </Typography>
            </Box>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Box
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 20px 50px rgba(15, 23, 42, 0.08)',
                border: '1px solid #E2E8F0',
                background: 'white',
              }}
            >
              <Box
                component="img"
                src="/images/connect-data-wizard.png"
                alt={t('connect.imageAlt')}
                loading="lazy"
                sx={{ width: '100%', display: 'block' }}
              />
            </Box>
            <Typography
              align="center"
              sx={{ mt: 1.5, color: '#94A3B8', fontSize: '0.85rem', fontStyle: 'italic' }}
            >
              {t('connect.imageCaption')}
            </Typography>
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
};

export default ConnectData;
